import { APIFieldAttributes, ArdoqId, ViewIds } from '@ardoq/api-types';
import { PagesViewModel, PagesViewSettings } from 'tabview/pagesView/types';
import { getViewModel$ } from './viewModel$';
import styled, { createGlobalStyle } from 'styled-components';
import { SettingsBar, settingsBarConsts } from '@ardoq/settings-bar';
import { Component, createRef, type ReactNode } from 'react';
import { BulkHoverPopover } from '@ardoq/popovers';
import { WorkspacePage } from 'tabview/pagesView/WorkspacePage';
import { ComponentPage } from 'tabview/pagesView/ComponentPage';
import { resolveFieldPopover } from 'tabview/pagesView/FieldLabel';
import { resolveTagPopover } from 'tabview/pagesView/Tags';
import { trackExportingVisualization } from 'tracking/events/visualizations';
import { TrackingExportFormat } from '@ardoq/export';
import { InfiniteScroll } from 'atomicComponents/InfiniteScroll/InfiniteScroll';
import { getLeftMenu, getRightMenu } from 'tabview/pagesView/menu';
import { PresentationNavigator } from 'tabview/pagesView/PresentationNavigator';
import { isPresentationMode } from 'env';
import { isSafari, classes } from '@ardoq/common-helpers';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { pagesContextMenuName } from './contextMenu';
import { EmptyState as EmptyStateBase } from './EmptyState';
import { DEFAULT_FIELDS } from './defaultFields';
import {
  ViewLegend,
  getActiveConditionalFormattingForLegend,
} from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { HeightOffset } from 'views/viewLegend/useViewLegendSubscription';
import { ViewLegendContainer } from '@ardoq/graph';
import { CommonDropdownOptions } from '@ardoq/global-consts';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.PAGESVIEW;

const executePrintOperation = () => {
  if (isSafari()) {
    // window.print() didn't work for Safari
    document.execCommand('print', false);
  } else {
    window.print();
  }
};

const PrintableContainer = createGlobalStyle`
    @media print {
      body,
      body :has(.PagesView.printable),
      .PagesView.printable,
      .PagesView.printable * {
        overflow: visible !important;
      }
      body *:not(
        .PagesView.printable,
        .PagesView.printable *,
        :has(.PagesView.printable)
      ) {
        display: none !important;
      }
      th,
      td {
        overflow-wrap: anywhere;
      }
      .PagesView.printable {
        width: 100%;
        zoom: 0.8;
      }
      .PagesView.printable :is(h1, h2, h3, h4) {
        break-inside: avoid;
      }
    }`;

const PagesViewContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: hidden;
  @media print {
    overflow: visible;
  }
`;

const InfiniteScrollWrapper = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  @media print {
    overflow: visible;
  }
`;

const NavbarAndContentContainer = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: row;
  flex: 1;
  @media print {
    overflow: visible;
  }
`;

const includedFields =
  (viewSettings: PagesViewSettings) =>
  (
    field: Pick<APIFieldAttributes, 'name'> & { deselectedByDefault?: boolean }
  ) => {
    if (
      field.deselectedByDefault &&
      !viewSettings.includeFields.includes(CommonDropdownOptions.ALL)
    ) {
      return viewSettings.includeFields.includes(field.name);
    }
    return (
      viewSettings.includeFields.includes(field.name) ||
      viewSettings.includeFields.includes(CommonDropdownOptions.ALL) ||
      viewSettings.includeFields.includes(
        CommonDropdownOptions.ALL_EXCEPT_DESELECTED_BY_DEFAULT
      )
    );
  };

type PageViewProps = PagesViewModel;

const resolveFieldOrTagPopover = (
  element: HTMLElement | SVGElement
): ReactNode | null =>
  resolveFieldPopover(element) ?? resolveTagPopover(element);

export class PagesView extends Component<PageViewProps> {
  state = {
    isPrintingMode: false,
  };
  popoverRootRef = createRef<HTMLDivElement>();
  printMql = window.matchMedia('print');

  printOrSaveToPdf = () => {
    trackExportingVisualization({
      name: VIEW_ID,
      format: TrackingExportFormat.PDF,
      isViewpointMode: false,
    });
    this.setState({
      isPrintingMode: true,
    });
  };

  safariBeforeAfterPrint = (mql: MediaQueryListEvent) => {
    if (!mql.matches) {
      // after print
      this.onAfterPrint();
    }
  };

  onAfterPrint = () => {
    this.setState({
      isPrintingMode: false,
    });
  };
  addPrintListeners = () => {
    if (!this.printMql.addEventListener) {
      // addListener() is deprecated but safari doesn't support addEventListener()
      this.printMql.addListener(mql => this.safariBeforeAfterPrint(mql));
    } else {
      window.addEventListener('afterprint', this.onAfterPrint);
    }
  };

  componentRenderFunction = (id: ArdoqId) => {
    const {
      referenceMap,
      referenceLabelById,
      componentsById,
      componentRepresentationById,
      componentPathById,
      componentDisplayLabelById,
      componentFillById,
      viewSettings,
      isViewpointMode,
      redactedFieldNames,
      canEditDescription,
      isReadOnlyVisualization,
      componentWriteAccessById,
      componentFieldsById,
      tagsByModelId,
      usersById,
      attachmentsById,
      i18nLocale,
      locale,
    } = this.props;

    const { isPrintingMode } = this.state;

    return (
      <ComponentPage
        key={id}
        component={componentsById[id]}
        componentPath={componentPathById.get(id)}
        displayLabel={componentDisplayLabelById.get(id) ?? ''}
        fillColor={componentFillById.get(id) ?? ''}
        expandDescription={viewSettings.expandDescription}
        bypassRenderLimit={isPrintingMode}
        defaultFields={DEFAULT_FIELDS.filter(includedFields(viewSettings))}
        fields={(componentFieldsById.get(id) ?? []).filter(
          includedFields(viewSettings)
        )}
        redactedFieldNames={redactedFieldNames}
        references={referenceMap.get(id)!}
        referenceLabelById={referenceLabelById}
        componentRepresentationById={componentRepresentationById}
        componentPathById={componentPathById}
        tagsByModelId={tagsByModelId}
        usersById={usersById}
        attachmentsById={attachmentsById}
        hideEmptyFields={viewSettings.hideEmptyFields}
        isLocalDateFormat={isPrintingMode}
        isViewpointMode={isViewpointMode}
        canEditDescription={canEditDescription}
        hasWriteAccess={componentWriteAccessById.get(id) ?? false}
        showContextMenu={
          !isPrintingMode && !isPresentationMode() && !isReadOnlyVisualization
        }
        i18nLocale={i18nLocale}
        locale={locale}
      />
    );
  };

  componentDidMount(): void {
    this.addPrintListeners();
  }

  componentWillUnmount(): void {
    if (!this.printMql.removeEventListener) {
      this.printMql.removeListener(this.safariBeforeAfterPrint);
    }
    window.removeEventListener('afterprint', this.onAfterPrint);
  }

  componentDidUpdate(
    _: PageViewProps,
    prevState: Readonly<{ isPrintingMode: boolean }>
  ) {
    if (this.state.isPrintingMode && !prevState.isPrintingMode) {
      executePrintOperation();
    }
  }

  render() {
    const {
      viewSettings,
      context,
      incomingReferenceTypes,
      outgoingReferenceTypes,
      componentIds,
      allComponentIds,
      filteredComponentIds,
      workspaceFields,
      scrollToViewComponent,
      topVisibleComponentId,
      canEditDescription,
      workspace,
      i18nLocale,
      navigatorItemDataById,
    } = this.props;
    const { isPrintingMode } = this.state;

    const containerClassNames = classes(
      'PagesView', // used with integration tests
      this.state.isPrintingMode ? 'printable' : null
    );

    const isEmptyView = !componentIds.length;

    const activeConditionalFormatting =
      getActiveConditionalFormattingForLegend(i18nLocale);

    const isLegendPossible = Boolean(
      activeConditionalFormatting.components.length ||
      activeConditionalFormatting.tags.length
    );

    const isLegendVisible =
      viewSettings[settingsBarConsts.IS_LEGEND_ACTIVE] &&
      isLegendPossible &&
      !isEmptyView;

    return (
      <>
        <PagesViewContainer
          className={containerClassNames}
          data-context-menu={pagesContextMenuName}
        >
          {!isPresentationMode() && (
            <SettingsBar
              viewId={VIEW_ID}
              i18nLocale={i18nLocale}
              leftMenu={getLeftMenu(
                viewSettings,
                incomingReferenceTypes,
                outgoingReferenceTypes,
                [...DEFAULT_FIELDS, ...workspaceFields],
                onViewSettingsUpdate,
                i18nLocale
              )}
              rightMenu={getRightMenu({
                viewSettings,
                isEmptyView,
                printOrSaveToPdf: this.printOrSaveToPdf,
                isLegendPossible: isLegendPossible,
                onViewSettingsUpdate,
                i18nLocale,
              })}
            />
          )}
          <NavbarAndContentContainer>
            {isPresentationMode() && (
              <PresentationNavigator
                componentIds={allComponentIds}
                filteredComponentIds={filteredComponentIds}
                workspaceId={context.workspaceId}
                topVisibleComponentId={topVisibleComponentId}
                navigatorItemDataById={navigatorItemDataById}
              />
            )}

            {isEmptyView ? (
              <EmptyStateBase />
            ) : (
              <InfiniteScrollWrapper ref={this.popoverRootRef}>
                <BulkHoverPopover
                  rootRef={this.popoverRootRef}
                  resolveContent={resolveFieldOrTagPopover}
                />
                <InfiniteScroll
                  workspaceId={context.workspaceId}
                  contextComponentId={context.componentId}
                  bypassRenderLimit={isPrintingMode}
                  items={componentIds}
                  itemRenderFunction={this.componentRenderFunction}
                  scrollToItem={scrollToViewComponent}
                  numberOfComponentsToRender={15}
                  shouldDispatchTopVisibleItem={true}
                  getRowElement={el =>
                    el instanceof HTMLDivElement &&
                    (el.classList.contains('pages-view-component-page') ||
                      el.dataset.clickNamespace ===
                        'pages view workspace header')
                      ? el
                      : null
                  }
                >
                  {workspace && (
                    <WorkspacePage
                      workspace={workspace}
                      bypassRenderLimit={isPrintingMode}
                      expandDescription={viewSettings.expandDescription}
                      shouldShowDescription={!context.componentId}
                      canEditDescription={canEditDescription}
                    />
                  )}
                </InfiniteScroll>
              </InfiniteScrollWrapper>
            )}
          </NavbarAndContentContainer>
          <HeightOffset>
            {heightOffset => (
              <ViewLegendContainer
                visible={isLegendVisible && !isPrintingMode}
                heightOffset={heightOffset}
              >
                <ViewLegend
                  componentTypes={[]}
                  activeDiffMode={null}
                  activeConditionalFormatting={activeConditionalFormatting}
                  labels={getViewLegendLabels(i18nLocale)}
                />
              </ViewLegendContainer>
            )}
          </HeightOffset>
        </PagesViewContainer>
        {this.state.isPrintingMode && <PrintableContainer />}
      </>
    );
  }
}

const ConnectedPagesView = connectInstanceWithProps(PagesView, getViewModel$);

export { ConnectedPagesView };
